import { TOPAZ_ERROR_REASONS, OverdueType, topazErrorReasonCodes } from './../../model/topaz'
import { PreferredCard, enrichCardTopazResult, isNonNullPreferredCard, selectPreferredCard } from './../../utils/credit-card'
import { preferredCardSelector, savedCardsSelector } from './../checkout/hooks'
import { valueIsNotNullOrUndefined } from './../../utils/validation'
import { EnrichedCardDetails, RetrieveCardsResponse } from './../../model/checkout'
import { AnyAction, AsyncThunk, ThunkDispatch, unwrapResult } from '@reduxjs/toolkit'
import { PortalApiError, CardDetails } from '@afterpaytouch/portal-api'
import {
  AsyncAction,
  CheckoutThunkConfig,
  portalAction,
  PortalThunk,
  selectPaymentMethod,
  setLastSuccessfulSetup,
  setupPayment,
  topazSelector,
  traceIdSelector as checkoutTraceId,
  CheckoutAppState,
  initiateNew,
  setupOrder,
  confirmNew,
  recoverableFlowTraceIdSelector,
  retrieveCards,
  setPreferredCard,
  setSavedCards,
  setPreferredCardError,
  TopazState,
  selectPaymentMethodWithoutStatus,
  updateTopazApiErrorMessage,
} from '../'
import {
  FinaliseThreeDSRequest,
  InitiateThreeDSRequest,
  InitiateThreeDSResponse,
  isNewCard,
  PaymentMechanism,
  PaymentMethodType,
  SelectedPaymentMethod,
  ThreeDSWorkflows,
  TopazSetupRequest,
  TopazStatus,
} from '../../model'
import { limitOnce } from '../../utils/limit'
import { compareObjects } from '../../utils/comparison'
import { handleTopazResultStatus, isValidTraceId } from './helpers'
import { getDeviceDetails } from '../../utils'
import { NewCard, ResultCode, TopazResult } from '@afterpaytouch/topaz-api'
import { CardFlowType } from '@afterpay/types'

type TopazThunk<Request, Response> = AsyncThunk<Response, Request, CheckoutThunkConfig>

export const setupPurchase: TopazThunk<PaymentMechanism, TopazResult> = portalAction('topaz/purchase', async (paymentMethod, thunk) => {
  const traceId = checkoutTraceId(thunk.getState())
  if (!isValidTraceId(traceId)) {
    throw new Error('Can not call topaz without a valid trace id')
  }
  return await thunk.extra.topaz().post<TopazSetupRequest, TopazResult>('/topaz/paylater/purchase/setup', { traceId, ...paymentMethod })
})

export const selectPurchaseMethod =
  (paymentMethod?: SelectedPaymentMethod, setup: boolean = true): AsyncAction<TopazResult> =>
  async (dispatch, getState) =>
    await limitOnce('selectPurchaseMethod', async () => {
      const topazState = topazSelector(getState())
      const traceId = checkoutTraceId(getState())
      const preferredCard = preferredCardSelector(getState())
      let response = ResultCode.OK
      const currentSetup = {
        paymentMethod,
        traceId,
      }
      let setupPurchaseResult
      if (
        paymentMethod?.type === PaymentMethodType.CREDIT_CARD &&
        setup &&
        !compareObjects(topazState.lastSuccessfulSetup, currentSetup) &&
        'card' in paymentMethod
      ) {
        setupPurchaseResult = unwrapResult(await dispatch(setupPurchase(paymentMethod.card)))
        if (
          !topazState.hasUserInitiatedChangedPaymentMethod &&
          setupPurchaseResult.resultCode !== ResultCode.OK &&
          topazState.preferredCardError === undefined
        ) {
          if (preferredCard?.switched) {
            dispatch(setPreferredCardError(preferredCard?.reason ?? undefined))
          } else {
            const topazStatus = handleTopazResultStatus(setupPurchaseResult)
            const cardErrorReasonCode = topazErrorReasonCodes[topazStatus] ?? TOPAZ_ERROR_REASONS.UnacceptableCard
            dispatch(setPreferredCardError(cardErrorReasonCode))
          }
        }

        if (setupPurchaseResult.resultCode === ResultCode.OK) {
          dispatch(setLastSuccessfulSetup(currentSetup))
        } else {
          updateTopazApiErrorMessage('topazPurchaseSetupError')
        }

        response = setupPurchaseResult.resultCode
      }

      if (response === ResultCode.OK) {
        dispatch(selectPaymentMethod(paymentMethod))
      } else if (response === ResultCode.UNACCEPTABLE_CARD) {
        dispatch(selectPaymentMethodWithoutStatus(paymentMethod))
      }
      return setupPurchaseResult
    })

export const updateSavedCardsWithError =
  (topazStatus: TopazStatus, cardTokenToUpdate: string | undefined, savedCards: EnrichedCardDetails[]): AsyncAction<void> =>
  async (dispatch) => {
    if (topazStatus !== TopazStatus.Saved) {
      const cardErrorReasonCode = topazErrorReasonCodes[topazStatus] ?? TOPAZ_ERROR_REASONS.UnacceptableCard
      const updatedCards = savedCards.map((x) => (x.token === cardTokenToUpdate ? { ...x, error: cardErrorReasonCode } : x))
      dispatch(setSavedCards(updatedCards))
    }
  }

export const handlePreferredFallback =
  (fallback: EnrichedCardDetails, nextRoute): AsyncAction<void> =>
  async (dispatch, getState) => {
    const preferredCard = preferredCardSelector(getState())
    const savedCards = savedCardsSelector(getState())

    try {
      const result = await dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: fallback?.token } }))
      const topazStatusResult = handleTopazResultStatus(result)

      if (result.resultCode === ResultCode.OK) {
        await dispatch(updatePreferredCardWithError(preferredCard, savedCards))
        nextRoute()
      } else {
        await dispatch(updateSavedCardsWithError(topazStatusResult, fallback.token, savedCards))
      }
    } catch (e) {}
  }

export const updatePreferredCardWithError =
  (preferredCard: PreferredCard | undefined, savedCards: EnrichedCardDetails[]): AsyncAction<void> =>
  async (dispatch, getState) => {
    const topazStatus = topazSelector(getState())

    const ePreferredCard =
      preferredCard !== undefined && isNonNullPreferredCard(preferredCard?.card)
        ? enrichCardTopazResult(preferredCard.card, topazStatus.preferredCardError)
        : null
    const newPreferredCard = selectPreferredCard(ePreferredCard, savedCards)

    dispatch(setPreferredCard(newPreferredCard))
  }

export const initiateThreeDS: PortalThunk<InitiateThreeDSRequest, InitiateThreeDSResponse> = portalAction('threeDS/initiate', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/threeDS/initiate', request)
})

export const finaliseThreeDS: PortalThunk<FinaliseThreeDSRequest, void> = portalAction('threeDS/finalise', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/threeDS/finalise', request)
})

export const initiateThreeDSVerification =
  (traceID): AsyncAction<InitiateThreeDSResponse | PortalApiError> =>
  async (dispatch) => {
    try {
      return unwrapResult(await dispatch(initiateThreeDS({ traceID, saveCard: true, workflow: ThreeDSWorkflows.CHECKOUT })))
    } catch (error) {
      return error
    }
  }

export const finaliseThreeDSVerification =
  (threeDSId, traceID): AsyncAction<null | PortalApiError> =>
  async (dispatch) => {
    try {
      return unwrapResult(await dispatch(finaliseThreeDS({ threeDSId, traceID, workflow: ThreeDSWorkflows.CHECKOUT })))
    } catch (error) {
      return error
    }
  }

const handleExistingPayment = async (
  paymentMethod: SelectedPaymentMethod | undefined,
  setup: boolean,
  traceId: string,
  dispatch: ThunkDispatch<CheckoutAppState, any, AnyAction>,
  topazState: TopazState
): Promise<ResultCode> => {
  let response = ResultCode.OK
  const currentSetup = {
    paymentMethod,
    traceId,
  }
  if (!isValidTraceId(traceId)) {
    throw new Error('Can not call topaz without a valid trace id')
  }

  if (
    paymentMethod?.type === PaymentMethodType.CREDIT_CARD &&
    setup &&
    !compareObjects(topazState.lastSuccessfulSetup, currentSetup) &&
    'card' in paymentMethod
  ) {
    const payload: TopazSetupRequest = {
      traceId,
      ...paymentMethod.card,
    }

    const { resultCode } = await dispatch(setupPayment.initiate(payload)).unwrap()

    if (resultCode === ResultCode.OK) {
      dispatch(setLastSuccessfulSetup(currentSetup))
    } else {
      updateTopazApiErrorMessage('topazPaymentSetupError')
    }
    response = resultCode
  }

  if (response === ResultCode.OK) {
    dispatch(selectPaymentMethod(paymentMethod))
  }

  return response
}

const handleNewPayment = async (
  paymentMethod: SelectedPaymentMethod | undefined,
  dispatch: ThunkDispatch<CheckoutAppState, any, AnyAction>,
  topazState: TopazState,
  shouldRefreshSavedCards: boolean = false,
  isThreeDSEnabled: boolean = false,
  flow: CardFlowType = CardFlowType.DEFAULT
): Promise<ResultCode> => {
  let response = ResultCode.OK
  let refreshCardsList: RetrieveCardsResponse | undefined

  const initiateResponse = await dispatch(
    initiateNew.initiate({
      deviceDetails: getDeviceDetails(),
    })
  ).unwrap()
  if (
    typeof initiateResponse?.traceId !== 'undefined' &&
    paymentMethod?.type === PaymentMethodType.CREDIT_CARD &&
    !compareObjects(topazState.lastSuccessfulSetup, paymentMethod) &&
    'card' in paymentMethod &&
    isNewCard(paymentMethod.card)
  ) {
    const newCard: NewCard = paymentMethod.card
    const currentSetup = {
      paymentMethod,
      traceId: initiateResponse.traceId,
    }

    const { resultCode } = await dispatch(
      setupOrder.initiate({
        ...newCard,
        traceId: initiateResponse.traceId,
      })
    ).unwrap()

    if (resultCode === ResultCode.OK) {
      !isThreeDSEnabled &&
        (await dispatch(
          confirmNew.initiate({
            traceId: initiateResponse.traceId,
            cardholderName: paymentMethod.card.cardHolderName,
            preferred: true,
            flow,
          })
        ).unwrap())

      if (shouldRefreshSavedCards) {
        refreshCardsList = await dispatch(retrieveCards()).unwrap()
      }

      dispatch(setLastSuccessfulSetup(currentSetup))
    } else {
      updateTopazApiErrorMessage('topazOrderSetupError')
    }

    response = resultCode
  }

  if (response === ResultCode.OK) {
    let paymentMethodSelected = paymentMethod
    if (shouldRefreshSavedCards) {
      const newTokenisedCard = getNewPreferredCardToken(refreshCardsList?.savedCards)
      if (newTokenisedCard !== null) {
        paymentMethodSelected = newTokenisedCard
      }
    }
    dispatch(selectPaymentMethod(paymentMethodSelected))
  }

  return response
}

const getNewPreferredCardToken = (savedCards: CardDetails[] = []): SelectedPaymentMethod | null => {
  const newPreferredCard = savedCards.find((card) => card.preferred)
  const selectedNewCard = valueIsNotNullOrUndefined(newPreferredCard)
    ? ({ card: { token: newPreferredCard?.token }, type: PaymentMethodType.CREDIT_CARD } as SelectedPaymentMethod)
    : null
  return selectedNewCard
}

export const selectRecoverableDeclinesPaymentMethod =
  (
    paymentMethod: SelectedPaymentMethod | undefined,
    setup = true,
    overdueType = OverdueType.ExistingPayment,
    isThreeDSEnabled = false
  ): AsyncAction<ResultCode> =>
  async (dispatch, getState) =>
    await limitOnce('selectRecoverableDeclinesPaymentMethod', async () => {
      const traceId = recoverableFlowTraceIdSelector(getState())
      const topazState = topazSelector(getState())

      if (overdueType === OverdueType.NewPayment) {
        return await handleNewPayment(paymentMethod, dispatch, topazState, false, isThreeDSEnabled)
      } else {
        return await handleExistingPayment(paymentMethod, setup, traceId, dispatch, topazState)
      }
    })

export const saveNewPaymentMethodCard =
  (paymentMethod: SelectedPaymentMethod, cardFlowType: CardFlowType): AsyncAction<ResultCode> =>
  async (dispatch, getState) =>
    await limitOnce('saveNewPaymentMethodCard', async () => {
      const topazState = topazSelector(getState())
      return await handleNewPayment(paymentMethod, dispatch, topazState, true, false, cardFlowType)
    })
